import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import { validatePassword } from 'components/FormPasswordCheckList'
import { EMAIL_IS_INVALID, PASSWORD_REQUIREMENTS } from 'const'
import { ECareLevel, FamilyRegisterRequest, UserType } from 'types'
import { boolean, object, string, TypeOf } from 'zod'

export interface FamilyFormValues {
  firstName: string
  lastName: string

  email: string

  personOfConcernFirstName: string
  personOfConcernLastNameOrInitial: string

  myNewPassword: string
  sameNewPassword: string

  levelOfCareNeeded: number | null
  readTermsAndConditions: boolean
}

export const familyFormSchema = object({
  firstName: string().min(1, 'First name is required'),
  lastName: string().min(1, 'Last name is required'),

  email: string().min(1, 'Email is required').email(EMAIL_IS_INVALID),

  personOfConcernFirstName: string().min(1, 'First name is required'),
  personOfConcernLastNameOrInitial: string().min(1, 'Last name is required'),

  myNewPassword: string().refine(
    (password: string) => validatePassword(password).allCheck,
    PASSWORD_REQUIREMENTS,
  ),
  sameNewPassword: string().min(1, 'Password is required'),

  levelOfCareNeeded: string().min(1, 'Level of care is required'),
  readTermsAndConditions: boolean().refine(
    (val) => val,
    'Please confirm of reading terms and conditions.',
  ),
}).refine((data) => data.myNewPassword === data.sameNewPassword, {
  path: ['sameNewPassword'],
  message: 'Passwords do not match',
})

export type FamilyFormType = TypeOf<typeof familyFormSchema>

/** Backend rejects an empty-string partnerCode — send undefined when blank. Codes are uppercase (PRT-...). */
export const normalizePartnerCode = (value?: string): string | undefined => {
  const trimmed = value?.trim().toUpperCase()
  return trimmed?.length ? trimmed : undefined
}

/** Registration without payment: the backend activates a partner-funded code inside this request. */
export const toFamilyRegisterRequest = (
  form: FamilyFormType,
  partnerCode?: string,
): FamilyRegisterRequest => ({
  accessCode: null,
  firstname: form.firstName,
  lastname: form.lastName,
  email: form.email,
  type: UserType.CAREGIVER,
  password: form.myNewPassword,
  personOfConcernFirstName: form.personOfConcernFirstName,
  personOfConcernLastName: form.personOfConcernLastNameOrInitial,
  careLevel: form.levelOfCareNeeded as ECareLevel,
  partnerCode: normalizePartnerCode(partnerCode),
})

/** Why an entered partner code still leads to payment, in the backend's own words. */
export interface PartnerCodeNotice {
  severity: 'info' | 'warning'
  message: string
}

const defaultValues: FamilyFormType = {
  firstName: '',
  lastName: '',

  email: '',

  personOfConcernFirstName: '',
  personOfConcernLastNameOrInitial: '',

  myNewPassword: '',
  sameNewPassword: '',

  levelOfCareNeeded: '',
  readTermsAndConditions: false,
}

export const familyInitialState = {
  familyForm: defaultValues,
  accessCode: undefined as string | undefined,
}

export const familyFormSettings = {
  mode: 'onChange' as const,
  resolver: zodResolver(familyFormSchema),
  defaultValues,
}

export enum FamilySignupStage {
  FamilyForm,
  // Code or card — organic registration and care-team invites without a Family seat alike.
  Choice,
  PartnerCode,
  CardForm,
}

// --- Invite (care giver) schema: no Person of Concern, no levelOfCare ---

export const inviteFamilyFormSchema = object({
  firstName: string().min(1, 'First name is required'),
  lastName: string().min(1, 'Last name is required'),
  email: string().min(1, 'Email is required').email(EMAIL_IS_INVALID),
  myNewPassword: string().refine(
    (password: string) => validatePassword(password).allCheck,
    PASSWORD_REQUIREMENTS,
  ),
  sameNewPassword: string().min(1, 'Password is required'),
  readTermsAndConditions: boolean().refine(
    (val) => val,
    'Please confirm of reading terms and conditions.',
  ),
}).refine((data) => data.myNewPassword === data.sameNewPassword, {
  path: ['sameNewPassword'],
  message: 'Passwords do not match',
})

export type InviteFamilyFormType = TypeOf<typeof inviteFamilyFormSchema>

export const inviteFamilyFormSettings = (email: string) => ({
  mode: 'onChange' as const,
  resolver: zodResolver(inviteFamilyFormSchema),
  defaultValues: {
    firstName: '',
    lastName: '',
    email,
    myNewPassword: '',
    sameNewPassword: '',
    readTermsAndConditions: false,
  },
})
